/**
 * Checkpoints — the definition of a station, not what is happening at it.
 *
 * Shapes follow the Notion Data Model, and match what `tools/seed` already
 * writes; the seed is not throwaway, so the two must agree. Passages, dwell
 * and occupancy are Stage B and are deliberately absent here.
 *
 * Everything in this file is a pure function over plain data. The client and
 * the Cloud Functions both need to agree about what a valid course is, and
 * this is the only place that answer lives.
 */
import { isFilled, isWholeNumberAtLeast, problem, } from './validation.js';
/**
 * The dwell cap actually in force, once the event default is applied.
 *
 * The null-inherits rule lives here and nowhere else. Both the HQ board and
 * whatever raises the alert must reach the same number, and a second copy of
 * `?? eventDefault` somewhere is how they eventually stop agreeing.
 */
export function effectiveMaxDwellMinutes(checkpoint, eventDefaultMinutes) {
    return checkpoint.maxDwellMinutes ?? eventDefaultMinutes;
}
/**
 * Whether understaffing at this checkpoint is worth telling HQ about.
 *
 * `unattended` means deliberately uncrewed, so silence is the correct
 * behaviour, not a missed alert. `intermittent` is crewed at peak only — it
 * can alert, but only inside its window, which is Stage B's problem.
 */
export function raisesStaffingAlerts(checkpoint) {
    return checkpoint.staffingModel !== 'unattended';
}
/** Sorted along the route. The course order is `routeOrder`, never `number`. */
export function inRouteOrder(checkpoints) {
    return [...checkpoints].sort((a, b) => a.routeOrder - b.routeOrder);
}
/**
 * One checkpoint on its own terms. Says nothing about its place in the course
 * — that is `validateCourse`, because it needs the others to compare against.
 */
export function validateCheckpoint(checkpoint) {
    const found = [];
    if (!isFilled(checkpoint.name)) {
        found.push(problem('name', 'error', 'Give the checkpoint a name.'));
    }
    if (!isWholeNumberAtLeast(checkpoint.number, 0)) {
        found.push(problem('number', 'error', 'The checkpoint number must be a whole number, 0 or more.'));
    }
    if (!isWholeNumberAtLeast(checkpoint.routeOrder, 0)) {
        found.push(problem('routeOrder', 'error', 'The route position must be a whole number, 0 or more.'));
    }
    if (!isFilled(checkpoint.physicalSiteId)) {
        found.push(problem('physicalSiteId', 'error', 'Every checkpoint belongs to a physical site.'));
    }
    if (checkpoint.maxDwellMinutes !== null && !isWholeNumberAtLeast(checkpoint.maxDwellMinutes, 1)) {
        found.push(problem('maxDwellMinutes', 'error', 'Maximum dwell must be at least one minute, or left to inherit the event default.'));
    }
    // Design rule 12, and the whole reason Tan Hill is in the seed. A sleep stop
    // on the event default will alarm on every sleeping runner, and a crew that
    // sees that once learns to ignore every alert for the rest of the night.
    if (checkpoint.isSleepStop && checkpoint.maxDwellMinutes === null) {
        found.push(problem('maxDwellMinutes', 'warning', 'This is a sleep stop with no dwell limit of its own, so it will use the event default and raise alerts on people who are simply asleep.'));
    }
    // Not an error: G4 is still deferred, so today every checkpoint is here.
    // It stays a warning because ambulance control is the consumer, and the
    // moment real locations exist this stops firing on its own.
    if (checkpoint.location === null) {
        found.push(problem('location', 'warning', 'No location recorded. Crews and ambulance control need a what3words or a grid reference.'));
    }
    return found;
}
/**
 * The course as a whole: the checks that need every checkpoint at once.
 *
 * Structural mistakes here are errors, because they make downstream logic
 * wrong rather than merely untidy — two checkpoints sharing a route position
 * gives no defined order, so auto-close and ETA have nothing to work from.
 * A missing finish is only a warning: a course under construction has one.
 */
export function validateCourse(checkpoints) {
    const found = [];
    found.push(...duplicatesOf(checkpoints, (c) => c.id, 'id', 'Two checkpoints share the id'));
    found.push(...duplicatesOf(checkpoints, (c) => String(c.number), 'number', 'Two checkpoints share the number'));
    found.push(...duplicatesOf(checkpoints, (c) => String(c.routeOrder), 'routeOrder', 'Two checkpoints share the route position'));
    const starts = checkpoints.filter((c) => c.role === 'start');
    const finishes = checkpoints.filter((c) => c.role === 'finish');
    if (starts.length === 0) {
        found.push(problem('role', 'error', 'The course has no start.'));
    }
    else if (starts.length > 1) {
        found.push(problem('role', 'error', `The course has ${String(starts.length)} starts. It can have one.`));
    }
    if (finishes.length > 1) {
        found.push(problem('role', 'error', `The course has ${String(finishes.length)} finishes. It can have one.`));
    }
    else if (finishes.length === 0 && checkpoints.length > 0) {
        found.push(problem('role', 'warning', 'The course has no finish yet.'));
    }
    // Warnings, not errors, and the distinction is load-bearing.
    //
    // A duplicate route position leaves the order genuinely undefined, so it
    // blocks. A finish that is not last is merely wrong: visible, correctable,
    // and downstream logic still has a defined order to work from. Blocking it
    // also creates a dead end — adding a checkpoint to a course that already has
    // a finish would be unsaveable until a *different* checkpoint was edited,
    // which cannot be done without leaving the screen and losing the draft.
    //
    // That is H7's show-do-not-police, and the same rule the rest of this file
    // follows: block only what is self-contradictory.
    //
    // Only meaningful once the roles themselves are sane, and only when there is
    // something to compare — reporting "the start is not first" on a course with
    // three starts is noise on top of the real problem.
    if (starts.length === 1 && checkpoints.length > 1) {
        const ordered = inRouteOrder(checkpoints);
        const first = ordered[0];
        if (first !== undefined && first.role !== 'start') {
            found.push(problem('routeOrder', 'warning', 'The start is not the first checkpoint on the route.'));
        }
    }
    if (finishes.length === 1 && checkpoints.length > 1) {
        const ordered = inRouteOrder(checkpoints);
        const last = ordered[ordered.length - 1];
        if (last !== undefined && last.role !== 'finish') {
            found.push(problem('routeOrder', 'warning', 'The finish is not the last checkpoint on the route.'));
        }
    }
    return found;
}
function duplicatesOf(checkpoints, key, field, lead) {
    const seen = new Map();
    for (const checkpoint of checkpoints) {
        const k = key(checkpoint);
        seen.set(k, (seen.get(k) ?? 0) + 1);
    }
    const found = [];
    for (const [value, count] of seen) {
        if (count > 1) {
            found.push(problem(field, 'error', `${lead} ${value}.`));
        }
    }
    return found;
}
/**
 * A readable, URL-safe id suggested from the number and name.
 *
 * The seed's convention — `cp1-tan-hill`, `cp0-st-bees` — and worth keeping.
 * An id appears in URLs, in log lines and in support conversations, so a
 * readable one is materially easier to work with than a ULID; checkpoints are
 * configuration created deliberately by one person, not concurrent writes
 * needing rule 6's idempotency.
 *
 * Only a suggestion: the organiser can change it before the checkpoint is
 * created, and never afterwards, because the id is the document path.
 */
export function suggestCheckpointId(number, name) {
    const slug = name
        .normalize('NFKD')
        // Strip accents, so Lordstones and Lôrdstones do not become two ids.
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/['’]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    const prefix = Number.isInteger(number) && number >= 0 ? `cp${String(number)}` : 'cp';
    return slug === '' ? prefix : `${prefix}-${slug}`;
}
/**
 * Ids we are willing to create. Firestore would accept far more; the
 * restriction is ours, so that two ids cannot differ only by case or a stray
 * space. Existing ids are not re-checked — this gates creation only.
 */
export function isUsableDocumentId(id) {
    return /^[a-z0-9][a-z0-9-]{1,62}$/.test(id);
}
//# sourceMappingURL=checkpoint.js.map